import type { CalendarDate } from '@internationalized/date';
import { useCallback, useMemo } from 'react';

import { useDatePickerConfig } from '../contexts/ConfigContext';
import { formatDateForLocale, parseDateForLocale } from '../utils';

/**
 * Hook that provides date parsing and formatting functions
 * using the locale and format from the DatePicker context
 *
 * This hook centralizes the logic of parsing and formatting dates
 * to avoid passing locale and format parameters everywhere
 */
export function useDateFormatter() {
  const { locale, dateInputCultureInfo } = useDatePickerConfig();

  /**
   * Parse a date string to CalendarDate using the configured locale and format
   * @param dateString - The date string to parse
   * @returns CalendarDate object or null if parsing failed
   */
  const parse = useCallback(
    (dateString: string): CalendarDate | null => {
      return parseDateForLocale(dateString, locale, dateInputCultureInfo);
    },
    [locale, dateInputCultureInfo],
  );

  /**
   * Format a CalendarDate to string using the configured locale and format
   * @param date - The CalendarDate to format
   * @returns Formatted date string
   */
  const format = useCallback(
    (date: CalendarDate): string => {
      return formatDateForLocale(date, locale, dateInputCultureInfo);
    },
    [locale, dateInputCultureInfo],
  );

  /**
   * Format a CalendarDate (or null) for display in the input field
   * @param date - The CalendarDate to format, or null for empty string
   * @returns Formatted date string or empty string
   */
  const formatForInput = useCallback(
    (date: CalendarDate | null): string => {
      if (!date) {
        return '';
      }

      return formatDateForLocale(date, locale, dateInputCultureInfo);
    },
    [locale, dateInputCultureInfo],
  );

  return useMemo(
    () => ({
      parse,
      format,
      formatForInput,
    }),
    [parse, format, formatForInput],
  );
}
